import { useEffect, useState } from "react";
import { HelpCircle } from "lucide-react";
import { Modal } from "./Modal";

interface Shortcut {
  /** Key/combo chips shown on the left. Strings render as <kbd>. */
  keys: string[];
  /** Separator between chips, e.g. "+" or "/". Defaults to none. */
  joiner?: string;
  description: string;
}

interface ShortcutSection {
  title: string;
  shortcuts: Shortcut[];
}

const SECTIONS: ShortcutSection[] = [
  {
    title: "Editing timeline hours",
    shortcuts: [
      {
        keys: ["Click"],
        description:
          "Edit a cell's hours. Saving fills that week and every week after it (fill-forward).",
      },
      {
        keys: ["⌘ / Ctrl", "Click"],
        joiner: "+",
        description: "Edit only the single week you clicked, leaving later weeks unchanged.",
      },
      {
        keys: ["Click", "Drag"],
        joiner: "+",
        description:
          "Drag across cells to select a range, then type a value to set hours for every selected cell at once.",
      },
      { keys: ["Enter"], description: "Save the hours you entered." },
      { keys: ["Esc"], description: "Cancel editing or clear the current selection." },
    ],
  },
  {
    title: "Editing names & details",
    shortcuts: [
      { keys: ["Click"], description: "Click a name or field to edit it inline." },
      { keys: ["Enter"], description: "Save your changes." },
      { keys: ["Esc"], description: "Cancel and revert to the original value." },
    ],
  },
  {
    title: "Assignments",
    shortcuts: [
      {
        keys: ["Enter", "Space"],
        joiner: "/",
        description: "Toggle confirmation on the focused assignment.",
      },
    ],
  },
  {
    title: "Dialogs & overlays",
    shortcuts: [
      { keys: ["Esc"], description: "Close the open dialog or overlay." },
      { keys: ["Click outside"], description: "Click the backdrop to close." },
      { keys: ["?"], description: "Open this shortcuts reference." },
    ],
  },
];

function isEditableTarget(el: EventTarget | null): boolean {
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement ||
    (el instanceof HTMLElement && el.isContentEditable)
  );
}

// Modal isn't reference-counted, so stacking a second one breaks its body
// scroll lock and Escape handling. Bail out if any modal is already mounted
// (this overlay included, which also makes the open triggers idempotent).
function isModalOpen(): boolean {
  return !!document.querySelector(".modal-overlay");
}

export function HelpOverlay() {
  const [open, setOpen] = useState(false);

  // Pressing "?" anywhere (outside of a text field) opens the cheatsheet.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "?" || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditableTarget(document.activeElement)) return;
      if (isModalOpen()) return;
      e.preventDefault();
      setOpen(true);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      <button
        type="button"
        className="help-button"
        onClick={() => {
          if (!isModalOpen()) setOpen(true);
        }}
        aria-label="Keyboard shortcuts"
        title="Keyboard shortcuts (?)"
      >
        <HelpCircle size={18} />
      </button>
      {open && (
        <Modal title="Keyboard shortcuts" size="medium" onClose={() => setOpen(false)}>
          <div className="help-shortcuts">
            {SECTIONS.map((section) => (
              <section key={section.title} className="help-section">
                <h3 className="help-section__title">{section.title}</h3>
                <ul className="help-section__list">
                  {section.shortcuts.map((shortcut, i) => (
                    <li key={i} className="help-row">
                      <span className="help-keys">
                        {shortcut.keys.map((key, j) => (
                          <span key={j} className="help-keys__item">
                            {j > 0 && shortcut.joiner && (
                              <span className="help-keys__joiner">{shortcut.joiner}</span>
                            )}
                            <kbd className="help-key">{key}</kbd>
                          </span>
                        ))}
                      </span>
                      <span className="help-desc">{shortcut.description}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}
